import React from 'react';
import Avatar from '../common/Avatar';
import { Task } from '../../types';

interface TasksTableProps {
  tasks?: Task[];
  onRowClick?: (taskId: string) => void;
  onEdit?: (taskId: string) => void;
  onDelete?: (taskId: string) => void;
}

const mockTasks: Task[] = [
  {
    id: '1',
    title: 'Client Meeting Preparation - Q1 Review',
    assignedTo: {
      id: 'user1',
      username: 'john.smith',
      email: 'john@example.com',
      initials: 'JS',
    },
    dueDate: '2024-01-15T09:00:00Z',
    status: {
      id: 'pending',
      label: 'Pending',
      color: '#A2B9B5',
    },
    priority: 'high',
  },
  {
    id: '2',
    title: 'Review Contract Terms',
    assignedTo: {
      id: 'user2',
      username: 'sarah.jones',
      email: 'sarah@example.com',
      initials: 'SJ',
    },
    dueDate: '2024-01-14T17:00:00Z',
    status: {
      id: 'in-progress',
      label: 'In Progress',
      color: '#EC8D18',
    },
    priority: 'urgent',
  },
  {
    id: '3',
    title: 'Prepare Court Filing Documents',
    assignedTo: {
      id: 'user3',
      username: 'mike.wilson',
      email: 'mike@example.com',
      initials: 'MW',
    },
    dueDate: '2024-01-20T10:00:00Z',
    status: {
      id: 'pending',
      label: 'Pending',
      color: '#A2B9B5',
    },
    priority: 'medium',
  },
  {
    id: '4',
    title: 'Update Client Database',
    dueDate: '2024-01-18T14:00:00Z',
    status: {
      id: 'completed',
      label: 'Completed',
      color: '#063932',
    },
    priority: 'low',
  },
];

const TasksTable: React.FC<TasksTableProps> = ({
  tasks = mockTasks,
  onRowClick,
  onEdit,
  onDelete,
}) => {
  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'urgent':
        return 'bg-danger/10 text-danger';
      case 'high':
        return 'bg-warning/10 text-warning';
      case 'medium':
        return 'bg-primary-light/40 text-primary-ink';
      case 'low':
        return 'bg-primary/10 text-primary-dark';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const isOverdue = (dueDate: string) => {
    return new Date(dueDate) < new Date();
  };

  return (
    <div className="dashboard-card rounded-2xl overflow-hidden">
      <div className="p-4 pb-0 mb-0 bg-transparent border-b-0 rounded-t-2xl">
        <h6 className="mb-0 font-semibold leading-normal text-lg text-slate-700 dark:text-slate-200">Tasks</h6>
      </div>
      <div className="flex-auto p-4">
        <div className="overflow-x-auto">
          <table className="table-auto w-full align-middle mb-0">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90">Title</th>
                <th className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90 ps-2">Assigned To</th>
                <th className="px-4 py-3 text-center align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90">Due Date</th>
                <th className="px-4 py-3 text-center align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90">Status</th>
                <th className="px-4 py-3 text-center align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90">Priority</th>
                <th className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-600/50">
              {tasks.map((task) => (
                <tr
                  key={task.id}
                  className="align-middle even:bg-slate-50 dark:even:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 cursor-pointer transition-colors"
                  onClick={() => onRowClick?.(task.id)}
                >
                  <td className="px-4 py-3 whitespace-nowrap align-middle">
                    <p className="text-xs font-bold text-slate-700 dark:text-slate-200 mb-0">{task.title}</p>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle">
                    {task.assignedTo ? (
                      <div className="flex items-center">
                        <Avatar user={task.assignedTo} size="small" className="mr-3" />
                        <div className="flex flex-col justify-center">
                          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{task.assignedTo.username}</span>
                          {task.assignedTo.email && <span className="text-xs text-slate-500 dark:text-slate-400">{task.assignedTo.email}</span>}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-500 dark:text-slate-400">Unassigned</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle text-center">
                    <div className="flex flex-wrap items-center justify-center gap-1.5">
                      <span className={`text-xs font-bold ${isOverdue(task.dueDate) ? 'text-danger' : 'text-slate-500 dark:text-slate-400'}`}>
                        {formatDate(task.dueDate)}
                      </span>
                      {isOverdue(task.dueDate) && (
                        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold text-danger bg-danger/10 rounded-full">Overdue</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle text-center">
                    <span
                      className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase rounded-full"
                      style={{
                        backgroundColor: task.status.color + '20',
                        color: task.status.color,
                      }}
                    >
                      {task.status.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle">
                    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => onEdit?.(task.id)} className="text-slate-500 font-bold text-xs hover:text-slate-700 dark:hover:text-slate-200" title="Edit user">Edit</button>
                      <button onClick={() => onDelete?.(task.id)} className="text-slate-500 font-bold text-xs hover:text-danger" title="Delete">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TasksTable;
